import axios from "axios";
import { getConfig } from "../configService";

const MAX_BUFFERED_EVENTS = 25;
const FLUSH_INTERVAL_MS = 5000;
const CLICK_DEDUP_WINDOW_MS = 250;

export class TelemetryService {
  static eventBuffer = [];
  static flushTimer = null;
  static unloadRegistered = false;
  static lastClickFingerprint = "";
  static lastClickAt = 0;

  static getBaseUrl() {
    if ((window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") && getConfig().localhostApi) {
      return "http://localhost:5055/v1";
    }

    return `${getConfig().apiBaseUrl}/metrics-collector/v1`;
  }

  static buildPayload(events) {
    return {
      app: "pairselfcare-web",
      environment: process.env.NODE_ENV || "unknown",
      events,
    };
  }

  static normalizeEvent(event) {
    return {
      name: event?.name || "unknown_event",
      page: event?.page || window.location.pathname || "unknown_page",
      route: event?.route || window.location.pathname || "unknown_route",
      status: event?.status || "ok",
      durationMs: Number.isFinite(event?.durationMs) ? Number(event.durationMs) : undefined,
      value: Number.isFinite(event?.value) ? Number(event.value) : undefined,
      timestampUtc: event?.timestampUtc || new Date().toISOString(),
      tags: event?.tags && typeof event.tags === "object" ? event.tags : undefined,
    };
  }

  static ensureFlushLoop() {
    if (TelemetryService.flushTimer) {
      return;
    }

    TelemetryService.flushTimer = setInterval(() => {
      TelemetryService.flush();
    }, FLUSH_INTERVAL_MS);

    if (!TelemetryService.unloadRegistered) {
      window.addEventListener("beforeunload", () => {
        TelemetryService.flush({ immediate: true });
      });

      window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          TelemetryService.flush({ immediate: true });
        }
      });

      TelemetryService.unloadRegistered = true;
    }
  }

  static shouldDropDuplicateClick(event) {
    if (event.name !== "button_click") {
      return false;
    }

    const fingerprint = `${event.route}|${event.tags?.button_text || ""}|${event.tags?.button_id || ""}|${event.tags?.button_type || ""}`;
    const now = Date.now();

    const isDuplicate =
      fingerprint === TelemetryService.lastClickFingerprint &&
      now - TelemetryService.lastClickAt <= CLICK_DEDUP_WINDOW_MS;

    TelemetryService.lastClickFingerprint = fingerprint;
    TelemetryService.lastClickAt = now;

    return isDuplicate;
  }

  static async sendEvent(event) {
    return TelemetryService.sendBatch([event]);
  }

  static async sendBatch(events) {
    if (!events || events.length === 0) {
      return;
    }

    TelemetryService.ensureFlushLoop();

    const normalized = events.map(TelemetryService.normalizeEvent).filter((event) => !TelemetryService.shouldDropDuplicateClick(event));
    if (normalized.length === 0) {
      return;
    }

    TelemetryService.eventBuffer.push(...normalized);

    if (TelemetryService.eventBuffer.length >= MAX_BUFFERED_EVENTS) {
      await TelemetryService.flush();
    }
  }

  static async flush(options = {}) {
    if (TelemetryService.eventBuffer.length === 0) {
      return;
    }

    const eventsToSend = TelemetryService.eventBuffer.splice(0, TelemetryService.eventBuffer.length);
    const payload = TelemetryService.buildPayload(eventsToSend);
    const endpoint = `${TelemetryService.getBaseUrl()}/telemetry/events`;

    if (options.immediate && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      const beaconSent = navigator.sendBeacon(endpoint, blob);
      if (beaconSent) {
        return;
      }
    }

    try {
      await axios.post(endpoint, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      // Requeue in-memory for retry on next flush cycle.
      TelemetryService.eventBuffer.unshift(...eventsToSend);
      console.error("Telemetry flush failed", error);
    }
  }
}
